import { syncDataLoaderFeature, selectionFeature } from "@headless-tree/core";
import { useState, useEffect } from "react";

import { NextRouter, useRouter } from "next/router";
import { Button } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useTree } from "@headless-tree/react";
import ChevronRightIcon from "@/icons/ChevronRightIcon";
import getExpandedTreePaths from "@/bundles/utils/getExpandedTreePaths";
import { TreeItem } from "@/bundles/types/TreeItem";
import { ContentOutline } from "@/bundles/types/ContentOutline";
import {
  container,
  header,
  treeContainer,
  treeItemCss,
  treeItemText,
  lineItemIndicatorCss,
  iconsContainer,
  chevronContainer,
  chevronContainerRotated,
  treeItemName,
} from "./GeneaologyPanel.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

type GeneaologyPanelProps = {
  contentOutline: ContentOutline;
  currentContentIdTree: string;
  updateTreeInURL: (router: NextRouter, tree: string) => void;
};

const GeneaologyPanel = ({
  contentOutline,
  currentContentIdTree,
  updateTreeInURL,
}: GeneaologyPanelProps) => {
  const router = useRouter();

  const treeItems = contentOutline?.items ?? {};
  const [focusedItemId, setFocusedItemId] = useState<string | null>(
    currentContentIdTree ?? contentOutline.initial_focused_item_id ?? null,
  );
  const [expandedItems, setExpandedItems] = useState<string[]>(
    getExpandedTreePaths(currentContentIdTree),
  );

  // This function handles the selection of items by user clicks. The selection
  // must change both the focused item and the URL query parameter `tree`.
  // Because of `useTree`, we need to make this function behave like a standard
  // React `useState` setter. It accepts a value or a function that updates the
  // value.
  const handleItemSelection = (
    updaterOrValue: string | null | ((prev: string | null) => string | null),
  ) => {
    const newItemId =
      typeof updaterOrValue === "function"
        ? updaterOrValue(focusedItemId)
        : updaterOrValue;
    setFocusedItemId(newItemId);
    if (newItemId) {
      updateTreeInURL(router, newItemId);
    }
  };

  // This hook keeps only a single content path expanded in the side-panel
  // content tree. It also makes sure that the focused item corresponds to the
  // current URL query parameter `tree`.
  useEffect(() => {
    setExpandedItems(getExpandedTreePaths(currentContentIdTree));
    setFocusedItemId(currentContentIdTree);
  }, [currentContentIdTree]);

  const tree = useTree<TreeItem>({
    state: {
      focusedItem: focusedItemId,
      expandedItems,
    },
    setExpandedItems,
    setFocusedItem: handleItemSelection,
    rootItemId: contentOutline?.root_item_id,
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => Boolean(item.getItemData().isFolder),
    dataLoader: {
      getItem: (itemId) => {
        const item =
          // The fallback value prevents crashing the page in transitions. For
          // example, it prevents crashing the page when we click on cards and
          // then on the browser history button. In this situation, `itemId`
          // can be outdated and not exist in `treeItems`.
          treeItems[itemId] || treeItems[contentOutline.root_item_id];
        if (!item) throw new Error(`Tree item not found: ${itemId}`);
        return item;
      },
      getChildren: (itemId) => treeItems?.[itemId]?.children || [],
    },
    indent: 20,
    features: [selectionFeature, syncDataLoaderFeature],
  });

  useEffect(() => {
    tree.rebuildTree();
  }, [tree, treeItems]);

  if (
    !contentOutline ||
    !contentOutline.root_item_id ||
    !treeItems[contentOutline.root_item_id]
  ) {
    return null;
  }

  const rootItem = treeItems[contentOutline.root_item_id];
  if (!rootItem) return null;

  return (
    <div css={container}>
      <div
        css={header}
        onClick={() => {
          tree.setState((s) => ({
            ...s,
            focusedItem: null,
          }));
          setExpandedItems([]);
          handleItemSelection(contentOutline.root_item_id);
        }}
      >
        {rootItem.name}
      </div>
      <div {...tree.getContainerProps()} css={treeContainer}>
        {tree.getItems().map((item) => (
          <ButtonWithTrackEvent
            key={item.getId()}
            __cssFor={{ root: treeItemCss(item.isFocused()) }}
            {...item.getProps()}
            role="treeitem"
            aria-selected={item.isSelected()}
            actionName={actionNames.interact}
            objectType={objectTypes.content}
            dataProps={{
              "data-qa-selector": `navigation-tree-item-${item.getId()}`,
            }}
            streamProperties={{
              description: "Select item from outline tree on bundles page",
            }}
          >
            <div
              css={treeItemText}
              style={{
                marginLeft: `${item.getItemMeta().level * 15}px`,
              }}
            >
              <div css={iconsContainer}>
                <div css={lineItemIndicatorCss(item.isFocused())} />
                {item.getChildren().length > 0 && (
                  <div
                    css={
                      item.isExpanded()
                        ? chevronContainerRotated
                        : chevronContainer
                    }
                  >
                    <ChevronRightIcon
                      css={chevronContainer}
                      strokeWidth="4px"
                    />
                  </div>
                )}
              </div>
              <span css={treeItemName}>{item.getItemName()}</span>
            </div>
          </ButtonWithTrackEvent>
        ))}
      </div>
    </div>
  );
};

export default GeneaologyPanel;
